import React, { useState, useContext, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Page,
  Select,
  Checkbox,
  Button,
  message,
  Modal,
  Form,
} from 'components';
import { QuickAssignmentAssignee, QuickLockboxAssignment } from '@bb/automaton';
import { AuthContext } from 'contexts';
import {
  getSupraLockboxData,
  PermissionsTypeV2,
  unassignSupraLockbox,
} from 'api/SupraLockboxController';
import { getProfile } from 'api/UserController';
import assignSupraLockboxV2 from 'api/SupraLockboxController/assignSupraLockboxV2';
import getSupraUsersByLocation from 'api/UserController/getSupraUsersByLocation';
import errorReporter from '@app/errorReporter';
import type {
  IGetSupraUsersByLocationQuery,
  SupraLockboxJsonType,
} from '@/app-package';
import { featureEnabled } from 'utils';
import KeyholderSearch from './KeyholderSearch';
import LockboxSearch from './LockboxSearch';
import LockboxTable, { OwnerAndAssigneeData } from './LockboxTable';
import theme from '../../../components/theme/index';

const QuickLockboxAssignmentPage = () => {
  const { auth } = useContext(AuthContext);
  const assignmentTypeLabelMap = {
    [QuickLockboxAssignment.owner]: 'Owner',
    [QuickLockboxAssignment.assignee]: 'Assignee',
    [QuickLockboxAssignment.ownerAndAssignee]: 'Owner and Assignee',
  };

  const ownerOrAssigneeLabelMap = {
    [QuickAssignmentAssignee.office]: 'Branch',
    [QuickAssignmentAssignee.user]: 'User',
  };

  const defaultAssignmentType = featureEnabled(
    'improvedLockboxAssignmentFeature',
  )
    ? auth.org.quickLockboxAssignment || null
    : auth.org.quickLockboxAssignment || QuickLockboxAssignment.owner;
  const [assignmentType, setAssignmentType] = useState(defaultAssignmentType);
  const [assigneeCheckbox, setAssigneeCheckbox] = useState(
    defaultAssignmentType === QuickLockboxAssignment.assignee ||
      defaultAssignmentType === QuickLockboxAssignment.ownerAndAssignee,
  );
  const [ownerCheckbox, setOwnerCheckbox] = useState(
    defaultAssignmentType === QuickLockboxAssignment.owner ||
      defaultAssignmentType === QuickLockboxAssignment.ownerAndAssignee,
  );

  const defaultAssigneeType = auth.org.quickAssignmentAssignee || null;
  const defaultOwnerType = auth.org.quickAssignmentOwner || null;

  const [officePerm, setOfficePerm] = useState(false);
  const [values, setValues] = useState<string[]>([]);
  const [searchOptions, setSearchOptions] = useState<SupraLockboxJsonType[]>(
    [],
  );

  const [lockboxes, setLockboxes] = useState<SupraLockboxJsonType[]>([]);
  const [lockboxesCount, setLockboxesCount] = useState(0);
  const [assignedLockboxes, setAssignedLockboxes] = useState<
    SupraLockboxJsonType[]
  >([]);
  const [assignedLockboxesCount, setAssignedLockboxesCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingData, setLoadingData] = useState(false);
  const [ownerValues, setOwnerValues] = useState<OwnerAndAssigneeData>();
  const [assigneeValues, setAssigneeValues] = useState<OwnerAndAssigneeData>();
  const [processing, setProcessing] = useState<boolean>();
  const [lockboxSearchLoading, setLockboxSearchLoading] = useState(false);
  const [deferredLockboxValues, setDeferredLockboxValues] = useState<
    string[] | null
  >(null);
  const [lockboxSearchQuery, setLockboxSearchQuery] = useState('');
  const [assigneeSelectValue, setAssigneeSelectValue] = useState(undefined);
  const [ownerSelectValue, setOwnerSelectValue] = useState(undefined);
  const [ownerResetType, setOwnerResetType] = useState(false);
  const [assigneeResetType, setAssigneeResetType] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [scannedLockboxesChanged, setScannedLockboxesChanged] = useState(false);

  const fetchLockboxData = useCallback(async () => {
    const boxes = await getSupraLockboxData({
      ownerId:
        ownerValues?.type === 'office'
          ? ownerValues?.subjectOrgMlsId
          : ownerValues?.subjectId,
      ownerType: ownerValues?.type,
      ownerBranchIndex: ownerValues?.location,
      assigneeId:
        assigneeValues?.type === 'office'
          ? assigneeValues?.subjectOrgMlsId
          : assigneeValues?.subjectId,
      assigneeType: assigneeValues?.type,
      assigneeBranchIndex: assigneeValues?.location,
      strictOwnershipMatching: true,
      requireOwnerAsMLS: false,
      filterBySupraOwnerMapping: true,
      page,
    });
    setAssignedLockboxes(boxes.lockboxes);
    setAssignedLockboxesCount(boxes.lockboxCount);
  }, [ownerValues, assigneeValues, page]);

  const getUserIds = async (
    orgId: string,
    location?: string,
    address?: string,
    userId?: string,
  ) => {
    const SEARCH_QUERY: IGetSupraUsersByLocationQuery = {
      orgId,
      location,
      address,
      eligibleGranteesOnly: true,
    };

    const result: string[] = (await getSupraUsersByLocation(SEARCH_QUERY))
      .filter(entry => entry.id !== userId)
      .map(u => u.id);

    return result;
  };

  const getUserDataById = async (userId: string) => {
    const result = await getProfile(userId, {});

    return result;
  };

  const addLockboxes = useCallback(
    (lockboxValues: string[]) => {
      try {
        const selectedLockboxes: SupraLockboxJsonType[] = [];
        const assignedSelectedLockboxes: SupraLockboxJsonType[] = [];
        lockboxValues.forEach(value => {
          const lockbox = searchOptions.find(
            option => option.serialNumber === value,
          );
          if (!lockbox) {
            const notFoundBox = generateNotFoundBox(value);
            selectedLockboxes.push(notFoundBox);
            return;
          }
          const assignee = lockbox.assignee as unknown as {
            _id: string;
            firstName: string;
            lastName: string;
          };
          const owner = lockbox.owner as unknown as {
            _id: string;
            firstName: string;
            lastName: string;
          };
          let assigned = false;
          switch (assignmentType) {
            case 'assignee': {
              if (
                assignee &&
                assigneeValues?.subjectId &&
                // eslint-disable-next-line no-underscore-dangle
                assignee._id === assigneeValues.subjectId
              ) {
                assignedSelectedLockboxes.push(lockbox);
                assigned = true;
              }
              break;
            }
            case 'owner': {
              if (
                owner &&
                ownerValues?.subjectId &&
                // eslint-disable-next-line no-underscore-dangle
                owner._id === ownerValues.subjectId
              ) {
                assignedSelectedLockboxes.push(lockbox);
                assigned = true;
              }
              break;
            }
            case 'ownerAndAssignee': {
              if (
                owner &&
                ownerValues?.subjectId &&
                // eslint-disable-next-line no-underscore-dangle
                owner._id === ownerValues.subjectId &&
                assignee &&
                assigneeValues?.subjectId &&
                // eslint-disable-next-line no-underscore-dangle
                assignee._id === assigneeValues.subjectId
              ) {
                assignedSelectedLockboxes.push(lockbox);
                assigned = true;
              }
              break;
            }
            default: {
              break;
            }
          }
          if (!assigned) selectedLockboxes.push(lockbox);
        });
        const newLockboxes = [...lockboxes];
        newLockboxes.push(
          ...selectedLockboxes.filter(
            lockbox =>
              !lockboxes.find(box => box.serialNumber === lockbox.serialNumber),
          ),
        );
        const newAssignedLockboxes = [...assignedLockboxes];
        newAssignedLockboxes.push(
          ...assignedSelectedLockboxes.filter(
            lockbox =>
              !assignedLockboxes.find(
                box => box.serialNumber === lockbox.serialNumber,
              ),
          ),
        );
        setLockboxes(newLockboxes);
        setLockboxesCount(newLockboxes.length);
        setAssignedLockboxes(newAssignedLockboxes);
        setAssignedLockboxesCount(newAssignedLockboxes.length);
        message.success('Lockboxes added');

        setValues([]);
      } catch (err) {
        message.error(err.message);
      }
    },
    [
      lockboxes,
      assignedLockboxes,
      ownerValues,
      assigneeValues,
      assignmentType,
      searchOptions,
    ],
  );

  const onLockboxValueChange = useCallback(
    (lockboxValues: string[]) => {
      if (!lockboxSearchLoading) {
        addLockboxes(lockboxValues);
        setLockboxSearchQuery('');
      }
      // If lockbox search is still loading, defer adding lockboxes until it's done
      else {
        setDeferredLockboxValues(lockboxValues);
      }
    },
    [lockboxSearchLoading, addLockboxes, setLockboxSearchQuery],
  );

  useEffect(() => {
    if (!lockboxSearchLoading && deferredLockboxValues) {
      addLockboxes(deferredLockboxValues);
      setDeferredLockboxValues(null); // Clear deferred values after processing
      setLockboxSearchQuery('');
    }
  }, [lockboxSearchLoading, deferredLockboxValues, addLockboxes]);

  const generateNotFoundBox = (value: string): SupraLockboxJsonType => ({
    _id: 'notFound',
    serialNumber: value,
    brand: 'supraLockbox',
    createdAt: 'now',
    ownershipStatus: 'Purchased',
    resaleStatus: 'Sold',
    lockboxStatus: 'New',
    productCode: 'iBox',
    sharingCode: '',
    supraOwnerId: 0,
    systemCode: '',
    updatedAt: 'now',
    version: '',
    timezoneOffset: 0,
    id: 'notFound',
  });

  const unassignLockbox = async (serialNumber: string, userId: string) => {
    try {
      await unassignSupraLockbox(serialNumber, userId);
      const filteredData = assignedLockboxes.filter(
        lockbox => lockbox.serialNumber !== serialNumber,
      );
      const assignedLockbox = assignedLockboxes.find(
        lockbox => lockbox.serialNumber === serialNumber,
      );

      if (assignedLockbox) {
        const newLockboxes = [...lockboxes];
        try {
          const lockboxData = await getSupraLockboxData({
            searchText: assignedLockbox.serialNumber,
            requireOwnerAsMLS: false,
            filterBySupraOwnerMapping: true,
          });
          const reloadedLockboxes = lockboxData.lockboxes;
          newLockboxes.push(...reloadedLockboxes);
          setLockboxes(newLockboxes);
          setLockboxesCount(newLockboxes.length);
        } catch (err) {
          message.error(err);
          errorReporter.error(err);
        }
      }
      setAssignedLockboxes(filteredData);
      setAssignedLockboxesCount(filteredData.length);
      message.success('Lockbox unassigned');
    } catch (err) {
      message.error(err.message);
    }
  };

  const deselectLockbox = async (serialNumber: string) => {
    try {
      const filteredData = lockboxes.filter(
        lockbox => lockbox.serialNumber !== serialNumber,
      );

      setLockboxes(filteredData);
      setLockboxesCount(lockboxesCount - 1);

      if (featureEnabled('improvedLockboxAssignmentFeature')) {
        setScannedLockboxesChanged(true);
      }
    } catch (err) {
      message.error(err.message);
    }
  };

  const onAssignClick = async () => {
    const granteeUserIds: string[] = [];

    try {
      setProcessing(true);
      // If office permissions are being granted, determine what user Ids need
      // to be granted permissions. For a user, this means all other users in
      // the same office. For a branch, it means all users in all offices under
      // that branch. For an office, it means all users in that office.
      if (officePerm) {
        message.info('Processing office-wide shackle permissions...');
        let userId: string | undefined;
        let orgId: string | undefined;
        let branchData: OwnerAndAssigneeData | undefined;
        let officeData: OwnerAndAssigneeData | undefined;

        if (!ownerValues) {
          // Owner Values cant be used to determine office(s) for shackle permissions
          // use Assignee values instead
          if (assigneeValues && assigneeValues.type === 'org') {
            orgId = assigneeValues.subjectId;
          }
          if (assigneeValues && assigneeValues.type === 'branch') {
            branchData = assigneeValues;
          }
          if (assigneeValues && assigneeValues.type === 'user') {
            userId = assigneeValues.subjectId;
          }
          if (assigneeValues && assigneeValues.type === 'office') {
            officeData = assigneeValues;
          }
        }
        if (ownerValues && ownerValues.type === 'org') {
          orgId = ownerValues.subjectId;
        }
        if (ownerValues && ownerValues.type === 'branch') {
          branchData = ownerValues;
        }
        if (ownerValues && ownerValues.type === 'user') {
          userId = ownerValues.subjectId;
        }
        if (ownerValues && ownerValues.type === 'office') {
          officeData = ownerValues;
        }

        if (userId) {
          const userData = await getUserDataById(userId);
          granteeUserIds.push(
            ...(await getUserIds(
              userData.org.toString(),
              userData.location,
              userData.address,
              userId,
            )),
          );
        }

        if (branchData) {
          // Calculate which offices are within this branch.
          // Branch data is given with location appended to end of Id.
          granteeUserIds.push(
            ...(await getUserIds(branchData.subjectId, branchData.location!)),
          );
        }

        // Find all userIds for users that are in the selected office
        if (featureEnabled('improvedLockboxAssignmentFeature') && officeData) {
          granteeUserIds.push(
            ...(await getUserIds(
              officeData.subjectId!,
              officeData.location!,
              officeData.address!,
            )),
          );
        }

        if (orgId) {
          granteeUserIds.push(...(await getUserIds(orgId)));
        }
      }

      // Finally, add the shackle permissions for the user assignees.
      if (
        ownerValues &&
        ownerValues.type === 'user' &&
        granteeUserIds.indexOf(ownerValues.subjectId) === -1
      )
        granteeUserIds.push(ownerValues.subjectId);
      if (
        assigneeValues &&
        assigneeValues.type === 'user' &&
        granteeUserIds.indexOf(assigneeValues.subjectId) === -1
      )
        granteeUserIds.push(assigneeValues.subjectId);

      let progress = 0;
      message.destroy(); // close the "Processing" message
      message.info('Assigning lockboxes...');
      await Promise.all(
        lockboxes.map(async lockbox => {
          try {
            const permissions: PermissionsTypeV2[] = [];
            if (ownerValues) {
              const permission: PermissionsTypeV2 = {
                // Use OrgMlsId for office assignments
                subjectId:
                  ownerValues.type === 'office'
                    ? ownerValues.subjectOrgMlsId
                    : ownerValues.subjectId,
                branchIndex: ownerValues.location || null,
                // eslint-disable-next-line no-underscore-dangle
                lockboxId: lockbox._id,
                permissionType: 'owner',
                ownerOrAssigneeType: ownerValues.type,
              };
              permissions.push(permission);
            }

            if (assigneeValues) {
              const permission: PermissionsTypeV2 = {
                subjectId:
                  assigneeValues.type === 'office'
                    ? assigneeValues.subjectOrgMlsId
                    : assigneeValues.subjectId,
                branchIndex: assigneeValues.location || null,
                // eslint-disable-next-line no-underscore-dangle
                lockboxId: lockbox._id,
                permissionType: 'assignee',
                ownerOrAssigneeType: assigneeValues.type,
              };
              permissions.push(permission);
            }

            if (permissions.length > 0) {
              await assignSupraLockboxV2({
                permissions,
                granteeUserIds,
                officePermissionsGenerated: officePerm,
              });
            }
            progress += 1;
          } catch (err) {
            message.error(err.message);
          }
        }),
      );
      const newLockboxes = [...assignedLockboxes, ...lockboxes];

      setAssignedLockboxes(newLockboxes);
      setAssignedLockboxesCount(newLockboxes.length);

      setLockboxes([]);
      setLockboxesCount(0);
      message.success(`${progress} lockboxes successfully assigned.`);
    } catch (err) {
      message.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const onNextClick = async () => {
    // Reset checkboxes back to defaults
    setOwnerCheckbox(
      defaultAssignmentType === QuickLockboxAssignment.owner ||
        defaultAssignmentType === QuickLockboxAssignment.ownerAndAssignee,
    );
    setAssigneeCheckbox(
      defaultAssignmentType === QuickLockboxAssignment.assignee ||
        defaultAssignmentType === QuickLockboxAssignment.ownerAndAssignee,
    );

    // Clear both dropdown selections
    setOwnerSelectValue(undefined);
    setAssigneeSelectValue(undefined);

    // Reset the default types back to org defaults so KeyholderSearch
    // components show org defaults again.

    setOwnerResetType(true);
    setAssigneeResetType(true);

    // Flip the reset flags back to false after a tick so children receive
    // the change and can reinitialize their internal state.
    setTimeout(() => {
      setOwnerResetType(false);
      setAssigneeResetType(false);
    }, 0);

    setAssignedLockboxes([]);
    setAssignedLockboxesCount(0);
    setLockboxes([]);
    setLockboxesCount(0);
    setScannedLockboxesChanged(true);
  };

  // When the assignmentType changes, clear out the current value of the unused type (get rid of the owner or assignee data that was chosen)
  // Update warning message accordingly.
  useEffect(() => {
    if (featureEnabled('improvedLockboxAssignmentFeature')) {
      switch (assignmentType) {
        case 'assignee':
          setOwnerValues(undefined);
          setWarningMessage(
            'Reassigning or unassigning the lockbox Assignee will result in the lockbox Owner being reset to the MLS, and will remove shackle permissions for the previous Owner and Assignee.',
          );
          break;
        case 'owner':
          setAssigneeValues(undefined);
          setWarningMessage(
            'Reassigning or unassigning the lockbox Owner will remove shackle permissions from previous Owner and Assignee.',
          );
          break;
        case 'ownerAndAssignee':
          setWarningMessage(
            'Owner and Assignee are both required in order to assign lockboxes.',
          );
          break;
        default:
          setWarningMessage(undefined);
          break;
      }
      setOwnerSelectValue(undefined);
      setAssigneeSelectValue(undefined);
    }
  }, [assignmentType]);

  // Old version that should be used when the improvedLockboxAssignmentFeature flag is off
  const onTypeChange = (
    newValue:
      | QuickLockboxAssignment
      | 'assignee'
      | 'owner'
      | 'ownerAndAssignee',
  ) => {
    if (newValue === 'owner') setAssigneeValues(undefined);
    if (newValue === 'assignee') setOwnerValues(undefined);
    setAssignmentType(newValue);
  };

  // Change the assignment type depending on which checkboxes are checked
  useEffect(() => {
    if (!featureEnabled('improvedLockboxAssignmentFeature')) return;

    if (assigneeCheckbox && ownerCheckbox) {
      setAssignmentType(QuickLockboxAssignment.ownerAndAssignee);
    } else if (assigneeCheckbox) {
      setAssignmentType(QuickLockboxAssignment.assignee);
    } else if (ownerCheckbox) {
      setAssignmentType(QuickLockboxAssignment.owner);
    } else {
      setAssignmentType(null);
    }
  }, [assigneeCheckbox, ownerCheckbox]);

  const setOwnerData = (data: string) => {
    if (!data) {
      setOwnerValues(undefined);
    } else {
      const splitVals = data.split('|');
      const branchSplitVals = splitVals[0].split('-');
      setOwnerValues({
        subjectId: branchSplitVals[0],
        type: splitVals[1] as 'org' | 'branch' | 'user' | 'office',
        location: branchSplitVals[1],
        // Include OrgMls document id and address index for office assignment
        ...(featureEnabled('improvedLockboxAssignmentFeature') && {
          subjectOrgMlsId: branchSplitVals[2],
          address: branchSplitVals[3],
        }),
      });
    }
    setOfficePerm(false);
  };

  const setAssigneeData = (data: string) => {
    if (!data) {
      setAssigneeValues(undefined);
    } else {
      const splitVals = data.split('|');
      const branchSplitVals = splitVals[0].split('-');
      setAssigneeValues({
        subjectId: branchSplitVals[0],
        type: splitVals[1] as 'org' | 'branch' | 'user' | 'office',
        location: branchSplitVals[1],
        ...(featureEnabled('improvedLockboxAssignmentFeature') && {
          subjectOrgMlsId: branchSplitVals[2],
          address: branchSplitVals[3],
        }),
      });
    }
    setOfficePerm(false);
  };

  const assignButtonDisabled = () =>
    (!ownerValues && !assigneeValues) ||
    (assignmentType === 'ownerAndAssignee' &&
      (!ownerValues || !assigneeValues)) ||
    lockboxes.length < 1 ||
    processing;

  const nextButtonDisabled = () =>
    (!ownerValues && !assigneeValues) ||
    (assignmentType === 'ownerAndAssignee' &&
      (!ownerValues || !assigneeValues)) ||
    assignedLockboxes.length < 1 ||
    processing;

  return (
    <Page
      type="filled"
      title="Quick Assignment Tool"
      backgroundColor="transparent"
    >
      <DetailText>
        Connect your scanner to scan lockboxes and select who to assign it to.
      </DetailText>
      <Section>
        {featureEnabled('improvedLockboxAssignmentFeature') ? (
          <StyledStack>
            <SectionHeader compact>Assignment Options</SectionHeader>
            <StyledDescriptionText>{warningMessage}</StyledDescriptionText>
          </StyledStack>
        ) : (
          <SectionHeader>User or Office ID</SectionHeader>
        )}
        {/* [OB-556] When removing the improvedLockboxAssignmentFeature flag, completely remove the StyledForm */}
        <StyledForm
          improvedLockboxAssigmentEnabled={featureEnabled(
            'improvedLockboxAssignmentFeature',
          )}
        >
          {!featureEnabled('improvedLockboxAssignmentFeature') && (
            <StyledSelect
              defaultValue={{
                label: assignmentTypeLabelMap[defaultAssignmentType],
                value: defaultAssignmentType,
              }}
              options={[
                {
                  label: assignmentTypeLabelMap[QuickLockboxAssignment.owner],
                  value: QuickLockboxAssignment.owner,
                },
                {
                  label:
                    assignmentTypeLabelMap[QuickLockboxAssignment.assignee],
                  value: QuickLockboxAssignment.assignee,
                },
                {
                  label:
                    assignmentTypeLabelMap[
                      QuickLockboxAssignment.ownerAndAssignee
                    ],
                  value: QuickLockboxAssignment.ownerAndAssignee,
                },
              ]}
              onChange={onTypeChange}
            />
          )}
          {featureEnabled('improvedLockboxAssignmentFeature') && (
            <>
              <StyledCheckbox
                defaultChecked={
                  defaultAssignmentType === QuickLockboxAssignment.owner ||
                  defaultAssignmentType ===
                    QuickLockboxAssignment.ownerAndAssignee
                }
                checked={ownerCheckbox}
                onChange={e => {
                  setOwnerCheckbox(!ownerCheckbox);
                }}
              >
                Owner
              </StyledCheckbox>
              <StyledCheckbox
                defaultChecked={
                  defaultAssignmentType === QuickLockboxAssignment.assignee ||
                  defaultAssignmentType ===
                    QuickLockboxAssignment.ownerAndAssignee
                }
                checked={assigneeCheckbox}
                onChange={e => {
                  setAssigneeCheckbox(!assigneeCheckbox);
                }}
              >
                Assignee
              </StyledCheckbox>
            </>
          )}

          {featureEnabled('improvedLockboxAssignmentFeature') ? (
            <>
              {ownerCheckbox && (
                <KeyholderSearch
                  searchLabel="owner"
                  defaultType={{
                    label: ownerOrAssigneeLabelMap[defaultOwnerType],
                    value: defaultOwnerType,
                  }}
                  disabled={
                    assignmentType === 'assignee' || assignmentType === null
                  }
                  value={ownerSelectValue}
                  assignmentType={assignmentType}
                  onValueChange={val => {
                    setOwnerData(val);
                    setOwnerSelectValue(val);
                  }}
                  resetToDefault={ownerResetType}
                  isMlsUser
                  style={{ marginBottom: '20px' }}
                />
              )}
              {assigneeCheckbox && (
                <KeyholderSearch
                  searchLabel="assignee"
                  defaultType={{
                    label: ownerOrAssigneeLabelMap[defaultAssigneeType],
                    value: defaultAssigneeType,
                  }}
                  value={assigneeSelectValue}
                  disabled={
                    assignmentType === 'owner' || assignmentType === null
                  }
                  onValueChange={val => {
                    setAssigneeData(val);
                    setAssigneeSelectValue(val);
                  }}
                  resetToDefault={assigneeResetType}
                  assignmentType={assignmentType}
                  isMlsUser
                  style={{ marginBottom: '20px' }}
                />
              )}
            </>
          ) : (
            <>
              <KeyholderSearch
                searchLabel="owner"
                isMlsUser
                disabled={assignmentType === 'assignee'}
                onValueChange={setOwnerData}
              />

              <KeyholderSearch
                searchLabel="assignee"
                isMlsUser
                disabled={assignmentType === 'owner'}
                onValueChange={setAssigneeData}
              />
            </>
          )}
          <Form.Item>
            <StyledCheckbox
              checked={officePerm}
              onChange={e => {
                const { checked } = e.target;
                if (
                  featureEnabled('LockboxSlowAssignmentEnhancements') &&
                  !officePerm &&
                  (ownerCheckbox || assigneeCheckbox)
                ) {
                  Modal.confirm({
                    title: `Grant shackle permissions to all subscribed users in ${ownerCheckbox && assigneeCheckbox ? "owner's" : ownerCheckbox ? "owner's" : "assignee's"} office?`,
                    content: (
                      <>
                        <p>
                          All subscribed users in the
                          {ownerCheckbox && assigneeCheckbox
                            ? " owner's "
                            : ownerCheckbox
                              ? " owner's "
                              : " assignee's "}
                          office will be granted shackle permissions for this
                          lockbox.
                        </p>
                        <p>
                          Users in the office who subscribe to their key in the
                          future, will automatically get shackle permissions. Do
                          you wish to continue?
                        </p>
                      </>
                    ),
                    okText: 'Assign',
                    cancelText: 'Cancel',
                    onOk: () => setOfficePerm(checked),
                  });
                } else {
                  setOfficePerm(checked);
                }
              }}
            >
              {featureEnabled('improvedLockboxAssignmentFeature') &&
              ownerCheckbox &&
              assigneeCheckbox
                ? "Grant Shackle permissions to everyone in owner's office"
                : 'Grant Shackle permissions to everyone in office'}
            </StyledCheckbox>
          </Form.Item>
        </StyledForm>
      </Section>
      <Section>
        <SectionHeader>Lockboxes</SectionHeader>
        <LockboxSearch
          values={values}
          setValues={setValues}
          onValueChange={onLockboxValueChange}
          options={searchOptions}
          setOptions={setSearchOptions}
          mlsStaffOrgId={auth?.org?.id}
          lockboxSearchLoading={lockboxSearchLoading}
          setLockboxSearchLoading={setLockboxSearchLoading}
          lockboxSearchQuery={lockboxSearchQuery}
          setLockboxSearchQuery={setLockboxSearchQuery}
          scannedLockboxesChanged={scannedLockboxesChanged}
          scannedLockboxesChangedCallback={setScannedLockboxesChanged}
          scannedLockboxes={lockboxes}
        />
        <TablesContainer>
          <FirstColumnContainer>
            <LockboxTable
              caption={'Scanned Lockboxes'}
              lockboxData={lockboxes}
              totalLockboxCount={lockboxesCount}
              unassignLockbox={unassignLockbox}
              deselectLockbox={deselectLockbox}
              page={page}
              setPage={setPage}
              pageSize={5}
              scrollHeight={240}
              loadingData={loadingData}
              showDeselect
            />
            <ButtonContainer>
              <StyledButton
                disabled={assignButtonDisabled()}
                type="primary"
                onClick={onAssignClick}
              >
                Assign Lockboxes
              </StyledButton>
            </ButtonContainer>
          </FirstColumnContainer>
          <StyledDiv>
            <FirstColumnContainer>
              <LockboxTable
                caption={'Assigned Lockboxes'}
                lockboxData={assignedLockboxes}
                totalLockboxCount={assignedLockboxesCount}
                unassignLockbox={unassignLockbox}
                deselectLockbox={deselectLockbox}
                page={page}
                setPage={setPage}
                pageSize={5}
                scrollHeight={700}
                loadingData={loadingData}
                assigneeSelection={assigneeValues}
                ownerSelection={ownerValues}
                showUnassign
              />

              {featureEnabled('improvedLockboxAssignmentFeature') && (
                <ButtonContainer>
                  <StyledButton
                    disabled={nextButtonDisabled()}
                    type="primary"
                    onClick={onNextClick}
                  >
                    Next
                  </StyledButton>
                </ButtonContainer>
              )}
            </FirstColumnContainer>
          </StyledDiv>
        </TablesContainer>
      </Section>
    </Page>
  );
};

const DetailText = styled.div`
  margin: 17px;
`;

export const Section = styled.div`
  width: 100%;
  max-width: 850px;
  background-color: white;
  border-radius: 8px;
  padding: 16px 16px 0 16px;
  margin: 16px;
`;

const StyledStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledDescriptionText = styled.p`
  color: ${theme.red5};
  font-size: 12px;
`;

export const PadRight = styled.div`
  padding-right: 30px;
`;

export const SectionHeader = styled.h1<{ compact?: boolean }>`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  margin: ${props => (props.compact ? '0' : '0 0 16px 0')};
`;

export const StyledForm = styled.div<{
  improvedLockboxAssigmentEnabled?: boolean;
}>`
  display: ${props => (!props.improvedLockboxAssigmentEnabled ? 'grid' : {})};
  grid-row-gap: ${props =>
    !props.improvedLockboxAssigmentEnabled ? '25px' : {}};
`;

const StyledSelect = styled(Select)`
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  color: #333;

  .ant-select-selector {
    background-color: white !important;
  }

  .ant-select-item-option-selected {
    background-color: white !important;
    color: #333 !important;
  }

  .ant-select-item-option-active {
    background-color: #e6e6e6 !important;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 17px;
`;

const TablesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr; // Two columns
  gap: 16px;
  width: 100%;
  height: 100%;
`;

const FirstColumnContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr auto; // The first row takes remaining space, the second row adjusts to content height
  height: 100%;
`;

const StyledDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  margin-top: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export default QuickLockboxAssignmentPage;
